import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@repo/ui/components/ui/pagination';
import { usePhotos } from '@/state/use-photos';

export const buildPageList = (
  current: number,
  total: number,
): (number | 'ellipsis')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index);

  const pages: (number | 'ellipsis')[] = [0];
  const start = Math.max(1, current - 1);
  const end = Math.min(total - 2, current + 1);

  if (start > 1) pages.push('ellipsis');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 2) pages.push('ellipsis');

  pages.push(total - 1);
  return pages;
};

export interface PaginationControlsProps {
  count: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
}

export const PaginationControls = ({
  count,
  page,
  limit,
  onPageChange,
}: PaginationControlsProps) => {
  const pageCount = Math.max(1, Math.ceil(count / limit));

  if (pageCount <= 1) return null;

  const goTo = (target: number) =>
    onPageChange(Math.min(Math.max(0, target), pageCount - 1));

  return (
    <Pagination className="mt-6">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 0) goTo(page - 1);
            }}
            className={page === 0 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>

        {buildPageList(page, pageCount).map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === 'ellipsis' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  goTo(item);
                }}
              >
                {item + 1}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < pageCount - 1) goTo(page + 1);
            }}
            className={
              page >= pageCount - 1 ? 'pointer-events-none opacity-50' : ''
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export const PhotoPagination = ({
  beforePageChange,
}: {
  beforePageChange?: () => boolean;
}) => {
  const { count, params, updatePagination } = usePhotos();
  return (
    <PaginationControls
      count={count}
      page={params.pagination.page}
      limit={params.pagination.limit}
      onPageChange={(page) => {
        if (
          page === params.pagination.page ||
          (beforePageChange && !beforePageChange())
        )
          return;
        updatePagination((pagination) => ({ ...pagination, page }));
      }}
    />
  );
};

export default PhotoPagination;
